import { datadogRum } from '@datadog/browser-rum';
import { useSession } from '@side/next-auth/react';
import { useEffect, useMemo } from 'react';
import { useInteractionsAgents } from '@/views/Interactions/hooks/useInteractionsAgents';
import { buildBrokerTeamsQuery } from '@/views/Interactions/hooks/useInteractionsAgents/buildAgentsQuery';
import { TEAM_ID_FILTER } from '@/views/Interactions/state/filters';
import { useIdentity } from '$api-clients/hooks';
import { Scopes } from '$authentication/useHasPermission/constants';
import { useHasPermission } from '$authentication/useHasPermission/useHasPermission';

/**
 * One page covers every broker today (the largest book is ≈437 teams), so team
 * options are materialized whole and filtered locally. The RUM action below is
 * what tells us that stopped being true and a paginated or server-side search is
 * needed.
 */
const TEAMS_PAGE_LIMIT = 500;

/**
 * The teams a broker reaches automatically.
 *
 * Broker-only by construction: the key is `null` for everyone else, so no request
 * is made and their teams come from the agent list alone.
 */
export function useInteractionsBrokerTeams() {
  const userId = useSession().data?.user?.id;

  const { hasPermission: isBroker } = useHasPermission({ roles: ['BROKER'] });
  const { hasPermission: isAdminBroker } = useHasPermission({
    roles: ['BROKER', 'ADMIN'],
    scopes: [Scopes.INTERACTIONS_READ],
  });

  const { data, error, isLoading } = useIdentity(
    '/teams',
    userId && isBroker
      ? {
          params: {
            query: {
              limit: TEAMS_PAGE_LIMIT,
              q: buildBrokerTeamsQuery({ userId, isAdmin: isAdminBroker }),
            },
          },
        }
      : null,
  );

  // `paging.next` is the only field that signals another page — `cursors.after`
  // is populated on every response, including the last.
  const hasMore = Boolean(data?.paging?.next);

  useEffect(() => {
    if (!hasMore) return;

    datadogRum.addAction('Interactions team filter exceeded one page', {
      limit: TEAMS_PAGE_LIMIT,
      userId,
    });
  }, [hasMore, userId]);

  const teams = useMemo(() => data?.data ?? [], [data]);

  return { teams, loading: isLoading, error } as const;
}

/**
 * Team filter options: the broker's reachable teams unioned with the teams of
 * whichever agents are loaded.
 *
 * The agent-derived half is what covers teams shared in by explicit permission
 * grants, which the broker-anchored query cannot see. It is also all a non-broker
 * gets — sound for them, since their agent list is never truncated.
 */
export function useInteractionsTeams() {
  const { agents } = useInteractionsAgents();
  const { teams: brokerTeams, loading } = useInteractionsBrokerTeams();

  const teams = useMemo(() => {
    const nameById = new Map<string, string>();

    // Ids are normalized the same way the agent mappers do it — legacy external
    // id when set — because that is the id space the facet values live in.
    for (const team of brokerTeams) {
      nameById.set(team.legacyExternalId ?? team.id, team.alias);
    }
    for (const agent of agents) {
      if (agent.teamId && agent.teamName && !nameById.has(agent.teamId)) {
        nameById.set(agent.teamId, agent.teamName);
      }
    }

    return [...nameById.entries()]
      .map(([teamId, teamName]) => ({
        label: teamName,
        value: `${TEAM_ID_FILTER}:${teamId}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [agents, brokerTeams]);

  return { teams, loading } as const;
}
